import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';

import { resolve } from '@/api/endPoints';
import AnalyticsNavbar from '@/components/generic/navbar';
import axiosClient from '@/utils/axios';
import { PATH_PAGE } from '@/routes/paths';

import { AD_CAMPAIGNS, AD_MANAGER } from '../dashboard/config';

type ActivityEventType =
  | 'created'
  | 'updated'
  | 'publish_requested'
  | 'published'
  | 'publish_failed'
  | string;

interface AdCampaignActivityEntry {
  uuid: string;
  created: string;
  event_type: ActivityEventType;
  event_type_label: string;
  actor_display_name: string;
  data: Record<string, any>;
}

interface ActivityResponse {
  campaign: {
    uuid: string;
    name: string;
    location_uuid: string;
  };
  results: AdCampaignActivityEntry[];
  next: string | null;
  previous: string | null;
}

// Keen ki-duotone icons per event type. The number of <span class="pathN"> children
// each glyph requires is baked into the Keen font — mismatched counts render blank.
const iconByEvent: Record<string, { icon: string; paths: number }> = {
  created: { icon: 'ki-abstract-26', paths: 2 },
  updated: { icon: 'ki-pencil', paths: 2 },
  publish_requested: { icon: 'ki-message-text-2', paths: 3 },
  published: { icon: 'ki-flag', paths: 2 },
  publish_failed: { icon: 'ki-disconnect', paths: 5 }
};

const EMPTY_VALUE_LABEL = '—';

// Human-readable labels for the fields captured in each activity snapshot.
// Order here drives the render order in the "saved configuration" panel.
const SNAPSHOT_FIELD_LABELS: Array<{ key: string; label: string }> = [
  { key: 'name', label: 'Campaign name' },
  { key: 'objective', label: 'Objective' },
  { key: 'placements', label: 'Placements' },
  { key: 'target_locations', label: 'Locations' },
  { key: 'radius_km', label: 'Radius (km)' },
  { key: 'target_countries', label: 'Target countries' },
  { key: 'target_languages', label: 'Languages' },
  { key: 'target_interests', label: 'Interests' },
  { key: 'target_behaviors', label: 'Behaviors' },
  { key: 'age_min', label: 'Minimum age' },
  { key: 'age_max', label: 'Maximum age' },
  { key: 'gender', label: 'Gender' },
  { key: 'special_ad_categories', label: 'Special ad categories' },
  { key: 'special_ad_category_country', label: 'Special ad category countries' },
  { key: 'pixel', label: 'Pixel' },
  { key: 'target_event', label: 'Conversion event' },
  { key: 'primary_text', label: 'Primary text' },
  { key: 'headline', label: 'Headline' },
  { key: 'landing_url', label: 'Landing URL' },
  { key: 'cta', label: 'Call to action' },
  { key: 'media_files', label: 'Media' },
  { key: 'budget_type', label: 'Budget type' },
  { key: 'budget_amount_cents', label: 'Budget (cents)' },
  { key: 'bid_strategy', label: 'Bid strategy' },
  { key: 'max_bid_amount_cents', label: 'Max bid (cents)' },
  { key: 'start_date', label: 'Start date' },
  { key: 'end_date', label: 'End date' },
  { key: 'dsa_beneficiary', label: 'DSA beneficiary' },
  { key: 'dsa_payor', label: 'DSA payor' }
];

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) return EMPTY_VALUE_LABEL;
  if (typeof value === 'string') return value.trim() ? value : EMPTY_VALUE_LABEL;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_VALUE_LABEL;
    return value
      .map((item) =>
        item && typeof item === 'object'
          ? (item as any).name || (item as any).repr || JSON.stringify(item)
          : String(item)
      )
      .join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (Object.keys(obj).length === 0) return EMPTY_VALUE_LABEL;
    if (typeof obj.name === 'string' && obj.name) return obj.name;
    if (typeof obj.repr === 'string' && obj.repr) return obj.repr;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatDateTime = (value: string, locale: string) => {
  try {
    return new Date(value).toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
};

const AdManagerActivityPage: React.FC = () => {
  const { t } = useTranslate();
  const { adUuid, locationslug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!adUuid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = resolve(
        'ad_campaign_draft_activity',
        { uuid: adUuid },
        {}
      );
      const response = await axiosClient.get<ActivityResponse>(url);
      setData(response.data);
    } catch (err: any) {
      const statusCode = err?.response?.status;
      if (statusCode === 404) {
        // Treat 404 as "no activity yet" — endpoint or campaign not yet in scope.
        setData({
          campaign: { uuid: adUuid, name: '', location_uuid: '' },
          results: [],
          next: null,
          previous: null
        });
      } else {
        setError(
          typeof err?.response?.data?.detail === 'string'
            ? err.response.data.detail
            : t(
                'adManager.activity.loadFailed',
                'Failed to load activity log.'
              )
        );
      }
    } finally {
      setLoading(false);
    }
  }, [adUuid, t]);

  const loadMore = useCallback(async () => {
    const nextUrl = data?.next;
    if (!nextUrl || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      // DRF returns fully-qualified next URLs; axios accepts them as-is.
      const response = await axiosClient.get<ActivityResponse>(nextUrl);
      setData((prev) =>
        prev
          ? {
              ...prev,
              results: [...prev.results, ...response.data.results],
              next: response.data.next,
              previous: response.data.previous
            }
          : response.data
      );
    } catch (err: any) {
      setError(
        typeof err?.response?.data?.detail === 'string'
          ? err.response.data.detail
          : t(
              'adManager.activity.loadMoreFailed',
              'Failed to load more activity.'
            )
      );
    } finally {
      setLoadingMore(false);
    }
  }, [data?.next, loadingMore, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const backToCampaign = () => {
    if (!locationslug || !adUuid) return;
    navigate(
      generatePath(PATH_PAGE.ad_manager_draft, {
        adUuid,
        locationslug
      })
    );
  };

  const navBarHeight =
    (document.getElementById('kt_app_header')?.clientHeight || 0) + 15 * 2 + 50;

  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en-US';

  const entries = data?.results ?? [];
  const campaignName = data?.campaign?.name?.trim() || '';

  return (
    <Container
      style={{ minHeight: `calc(100vh - ${navBarHeight}px)` }}
      className="w-100"
    >
      <div className="bg-light px-4 pt-2">
        <AnalyticsNavbar
          actions={[
            {
              label: AD_CAMPAIGNS,
              onClick: () => {
                if (!locationslug) return;
                navigate(
                  generatePath(PATH_PAGE.ad_analytics, { locationslug })
                );
              },
              translationFallback: 'Campaigns',
              updateHash: false
            },
            {
              label: AD_MANAGER,
              onClick: () => {
                if (!locationslug) return;
                navigate(
                  generatePath(PATH_PAGE.ad_manager, { locationslug })
                );
              },
              active: true,
              default: true,
              skipInitialOnClick: true,
              translationFallback: 'Ad Manager',
              updateHash: false
            }
          ]}
          className="rounded-3 lomavis-ad-manager-navbar"
        />
      </div>

      <div className="bg-light">
        <div className="container-xxl px-4 pt-3">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={backToCampaign}
          >
            <i className="fa-solid fa-chevron-left me-2" aria-hidden="true" />
            {t('adManager.activity.backToCampaign', 'Back to campaign')}
          </Button>
        </div>
      </div>

      <div className="bg-light pb-5">
        <div className="container-xxl px-4 py-4">
          <div className="rounded-4 border bg-white p-4 p-md-5">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-2 mb-4">
              <div>
                <div className="text-muted small fw-bold text-uppercase mb-1">
                  {t('adManager.activity.eyebrow', 'Campaign activity')}
                </div>
                <h1 className="fs-3 fw-bold text-gray-900 lh-sm mb-1">
                  {campaignName ||
                    t('adManager.activity.title', 'Activity log')}
                </h1>
                <div className="text-muted small">
                  {t(
                    'adManager.activity.subtitle',
                    'Every change and publishing event for this campaign, in order.'
                  )}
                </div>
              </div>
              {!loading && !error ? (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    void load();
                  }}
                >
                  <i
                    className="fa-solid fa-arrows-rotate me-2"
                    aria-hidden="true"
                  />
                  {t('adManager.activity.refresh', 'Refresh')}
                </Button>
              ) : null}
            </div>

            {loading ? (
              <div className="d-flex align-items-center gap-2 text-muted small py-5 justify-content-center">
                <Spinner animation="border" size="sm" />
                {t('adManager.activity.loading', 'Loading activity...')}
              </div>
            ) : error ? (
              <Alert
                variant="danger"
                className="mb-0 d-flex justify-content-between align-items-center gap-3"
              >
                <span>{error}</span>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    void load();
                  }}
                >
                  {t('common.retry', 'Retry')}
                </Button>
              </Alert>
            ) : entries.length === 0 ? (
              <div className="d-flex flex-column align-items-center text-center py-5 px-3">
                <div
                  aria-hidden
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{
                    background: '#f1f3f5',
                    color: '#6c757d',
                    height: 64,
                    width: 64,
                    fontSize: 24
                  }}
                >
                  <i className="fa-regular fa-clock" />
                </div>
                <div className="fw-semibold text-body mb-1">
                  {t('adManager.activity.empty.title', 'No activity yet')}
                </div>
                <div className="text-muted small" style={{ maxWidth: 420 }}>
                  {t(
                    'adManager.activity.empty.description',
                    'Changes to this campaign, publishing attempts, and outcomes will appear here as they happen.'
                  )}
                </div>
              </div>
            ) : (
              <div className="timeline timeline-border-dashed">
                {entries.map((entry) => {
                  const iconInfo =
                    iconByEvent[entry.event_type] || iconByEvent.updated;
                  const actor =
                    entry.actor_display_name?.trim() ||
                    t('adManager.activity.systemActor', 'System');
                  const snapshot: Record<string, unknown> =
                    entry.data?.snapshot &&
                    typeof entry.data.snapshot === 'object'
                      ? entry.data.snapshot
                      : {};
                  const failureMessage: string =
                    typeof entry.data?.failure_message === 'string'
                      ? entry.data.failure_message
                      : '';
                  const hasSnapshot = SNAPSHOT_FIELD_LABELS.some(
                    ({ key }) => key in snapshot
                  );

                  return (
                    <div className="timeline-item" key={entry.uuid}>
                      <div className="timeline-line" />
                      <div className="timeline-icon">
                        <i
                          className={`ki-duotone ${iconInfo.icon} fs-2 text-gray-500`}
                          aria-hidden="true"
                        >
                          {Array.from({ length: iconInfo.paths }).map(
                            (_, idx) => (
                              <span
                                key={idx}
                                className={`path${idx + 1}`}
                              />
                            )
                          )}
                        </i>
                      </div>
                      <div className="timeline-content mb-10 mt-n1">
                        <div className="pe-3 mb-5">
                          <div className="fs-5 fw-semibold mb-2">
                            {t(
                              `adManager.activity.event.${entry.event_type}`,
                              entry.event_type_label
                            )}
                          </div>
                          <div className="d-flex align-items-center mt-1 fs-6">
                            <div className="text-muted me-2 fs-7">
                              {formatDateTime(entry.created, locale)}
                              {' · '}
                              {t('adManager.activity.byActor', 'by')}
                            </div>
                            <span className="fw-semibold text-gray-800">
                              {actor}
                            </span>
                          </div>
                        </div>

                        {entry.event_type === 'publish_failed' &&
                        failureMessage ? (
                          <div className="overflow-auto pb-5">
                            <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed min-w-lg-600px p-4">
                              <i
                                className="ki-duotone ki-cross-circle fs-2tx text-danger me-4"
                                aria-hidden="true"
                              >
                                <span className="path1" />
                                <span className="path2" />
                              </i>
                              <div className="d-flex flex-column">
                                <div className="text-gray-900 fw-bold mb-1">
                                  {t(
                                    'adManager.activity.publishFailedTitle',
                                    'Publishing failed'
                                  )}
                                </div>
                                <div className="fs-6 text-gray-700">
                                  {failureMessage}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : hasSnapshot ? (
                          <div className="overflow-auto pb-5">
                            <details className="border border-dashed border-gray-300 rounded min-w-lg-600px px-7 py-4">
                              <summary
                                className="fs-6 fw-semibold text-primary"
                                style={{ cursor: 'pointer' }}
                              >
                                {t(
                                  'adManager.activity.viewSavedConfig',
                                  'View saved configuration'
                                )}
                              </summary>
                              <dl
                                className="small mt-3 mb-0"
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'auto 1fr',
                                  columnGap: 12,
                                  rowGap: 4
                                }}
                              >
                                {SNAPSHOT_FIELD_LABELS.filter(
                                  ({ key }) => key in snapshot
                                ).map(({ key, label }) => (
                                  <React.Fragment key={key}>
                                    <dt className="fw-semibold text-gray-800">
                                      {t(`adManager.fields.${key}`, label)}
                                    </dt>
                                    <dd
                                      className="text-gray-600 mb-0"
                                      style={{ wordBreak: 'break-word' }}
                                    >
                                      {formatChangeValue(snapshot[key])}
                                    </dd>
                                  </React.Fragment>
                                ))}
                              </dl>
                            </details>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {data?.next ? (
                  <div className="d-flex justify-content-center pt-3">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      disabled={loadingMore}
                      onClick={() => {
                        void loadMore();
                      }}
                    >
                      {loadingMore ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {t(
                            'adManager.activity.loadingMore',
                            'Loading more...'
                          )}
                        </>
                      ) : (
                        t('adManager.activity.loadMore', 'Load more')
                      )}
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default AdManagerActivityPage;
